// Mini modal with picture having a gray background around it (two "blocks")
import React, { useContext, useEffect, useRef } from 'react';
import { css, useTheme } from '@emotion/react';
import {
  type RestModalEmailSuccessModal,
  type RestModalForwards,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { cs } from '@bbkAdminUtils/css-utils';
import { type ReactFCC } from '@bbkAdminUtils/utility-types';
import CustomerHtml from '@bbkFunnel/components/customer_html/customer_html';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import ActionButtonIcons from '@bbkFunnelComponents/modals/sections/action-button-helpers';
import { FunnelButton } from '@bbkFunnelComponents/v2';
import { RefsContext } from '@bbkFunnelUtils/refs.context';
import {
  ModalDesktopInnerSpacer,
  OfferTitleMobile,
  TitleTheme,
  useMediaQueries,
} from '../sections';

type MessageSuccessActionsProps = {
  id: string;
  info: RestModalForwards;
  hide: React.MouseEventHandler;
  hideCancel: boolean;
  hideReturnBtn: boolean;
  onClick: Function;
};
export const MessageSuccessActions: ReactFCC<MessageSuccessActionsProps> = ({
  id,
  info,
  hide,
  hideCancel,
  hideReturnBtn,
  onClick,
}) => {
  const theme = useTheme() as EmotionTheme;
  const equalButtonSize = theme.complianceEqualButtonSize;
  const { mobileView } = useMediaQueries();
  const refsPrefix = id;
  const refsCtx = useContext(RefsContext);
  const btn1Ref = useRef<HTMLButtonElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-btn1`, btn1Ref), [btn1Ref]);
  const btn2Ref = useRef<HTMLButtonElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-btn2`, btn2Ref), [btn2Ref]);

  const btnTextStyle = {
    fontSize: `calc(${theme.buttonFontSize} * 1.1)`,
    display: 'inline-block',
    marginRight: '10px',
    marginLeft: '5px',
  };
  const btnSubTextStyle: React.CSSProperties = {
    display: 'inline-block',
    fontWeight: 'lighter',
    fontSize: `calc(${theme.buttonFontSize} * 0.9)`,
  };

  let labelText: React.ReactNode = info.text;
  if (info.subText) {
    labelText = (
      <span>
        <span style={btnTextStyle}>{info.text}</span>
        <span style={btnSubTextStyle}>{info.subText}</span>
      </span>
    );
  }

  const btnColor = theme.buttonBackgroundColor || theme.primaryColor;
  const backwardsButtonStyles = {
    background: 'transparent',
    border: `2px solid ${btnColor}`,
    marginTop: '1rem',
    width: '100%',
    color: btnColor,
  };
  {
    /* Added padding to make total 27px in mobile for equal size buttons */
  }
  return (
    <div
      className={cs('message-success-btns', {
        'tw-px-[11px]': equalButtonSize && mobileView,
        'tw-flex tw-justify-center': equalButtonSize && !mobileView,
      })}
    >
      {!hideReturnBtn && (
        <FunnelButton
          ref={btn1Ref}
          onClick={() => onClick({ excludeOfferAccepts: true })}
          disabled={info.disabled}
          classes="d-flex align-items-center justify-content-center"
          specificStyles={{ textAlign: 'left', width: '100%' }}
        >
          {info?.icon && <ActionButtonIcons icon={info.icon} />}
          {labelText}
        </FunnelButton>
      )}
      {!hideCancel && !info.hideCancelAction && (
        <FunnelButton
          ref={btn2Ref}
          onClick={hide}
          specificStyles={backwardsButtonStyles}
          classes="d-inline-flex align-items-center justify-content-center"
        >
          {info?.cancelIcon && <ActionButtonIcons icon={info.cancelIcon} />}
          <span style={{ paddingLeft: '5px' }}>
            {info.cancelText || 'Continue to cancel'}
          </span>
        </FunnelButton>
      )}
    </div>
  );
};

type Props = {
  modalInfo: RestModalEmailSuccessModal;
  close: () => void;
  handleAction: (
    forwards: RestModalForwards,
    extra?: { excludeOfferAccepts: boolean }
  ) => void;
};

export const MessageSuccess: React.FC<Props> = (props) => {
  const {
    modalInfo: {
      id,
      requires: {
        info: { title },
        hideCancel,
        hideReturnBtn,
        success: { title: successTitle, details },
        forwards,
      },
    },
    close,
    handleAction,
  } = props;
  const { mobileView } = useMediaQueries();

  const refsPrefix = id;
  const refsCtx = useContext(RefsContext);
  const titleRef = useRef<HTMLSpanElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-title`, titleRef), [titleRef]);
  const descRef = useRef<HTMLDivElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-desc`, descRef), [descRef]);

  return (
    <ModalDesktopInnerSpacer
      css={css`
        max-width: 600px;
        ${mobileView && 'padding: 12px 1rem; flex: 1 1 auto;'}
      `}
      className={cs('message-success', 'display-grid', 'inner-spacer')}
    >
      <OfferTitleMobile
        title={<TitleTheme title={title} ref={titleRef} />}
        close={close}
      />
      <div
        className="success-message"
        css={css`
          margin: 0 0 1rem 0;
        `}
        ref={descRef}
      >
        <div
          className="success-title"
          css={css`
            font-weight: bold;
            font-size: 1.35em;
            margin-bottom: 5px;
          `}
        >
          <CustomerHtml value={successTitle} />
        </div>
        <div>
          <CustomerHtml value={details} />
        </div>
      </div>
      <div>
        <MessageSuccessActions
          id={id}
          info={forwards}
          onClick={(extra?: { excludeOfferAccepts: boolean }) =>
            handleAction(forwards, extra)
          }
          hide={close}
          hideCancel={hideCancel === true}
          hideReturnBtn={hideReturnBtn === true}
        />
      </div>
    </ModalDesktopInnerSpacer>
  );
};
